import { useAuth0 } from '@auth0/auth0-react';
import { ErrorBanner, PageHeader } from '@my/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { CombinedHistoryButton } from '@/components/HistoryButtons';

/** Table names read as jargon here; these are what the sections are called. */
function configurationEntityLabel(entityType: string): string {
  switch (entityType) {
    case 'ServiceTag':
      return 'Service tag';
    case 'OrganisationEquipmentType':
      return 'Equipment type';
    case 'OrganisationCarrier':
      return 'Carrier';
    case 'OrganisationRoute':
      return 'Route';
    default:
      return entityType;
  }
}
import { CarriersAndRoutesSection } from './CarriersAndRoutesSection';
import { CopyFunctionsSection } from './CopyFunctionsSection';
import { EquipmentDefinitionsSection } from './EquipmentDefinitionsSection';
import { MenuVisibilitySection } from './MenuVisibilitySection';
import { PlanFeaturesSection } from './PlanFeaturesSection';
import { ServiceTagsSection } from './ServiceTagsSection';
import { ShippingIntegrationSection } from './ShippingIntegrationSection';
import { SustainabilitySection } from './SustainabilitySection';
import { WorkViewTabsSection } from './WorkViewTabsSection';

/**
 * Central Configuration page — TMS-side counterpart to My.Tms.Old's
 * `ConfigurationSettingsPage`. Service Tags → Equipment Definitions →
 * Carriers & Routes, then the support-only cards. Cargo Definitions moved to a
 * page of its own (Settings → Cargo Definitions, next in the menu); it still
 * picks from the service tags and equipment types kept here.
 *
 * Org id is resolved from Auth0 claims (stop-gap — see
 * `getCurrentOrganisationId`). Once per-org switching support lands in the
 * shared account store (`@my/platform/account`), this page (and
 * OrganisationPage) move to the shared store.
 */
export function CentralConfigurationPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { t } = useTranslation(['configuration', 'common']);

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader title={t('configuration:title')} description={t('configuration:description')}>
        {/* One button for the whole page rather than one per section. The
            sections are a layout; "what changed in the configuration" is a
            single question, and it spans both services — service tags are
            TMS, equipment types and carriers are Directory. Cargo types have
            their own page and their own history now. */}
        <CombinedHistoryButton
          organisationId={orgId}
          tmsTypes="ServiceTag"
          directoryTypes="OrganisationEquipmentType,OrganisationCarrier,OrganisationRoute"
          subject={t('configuration:title')}
          entityLabel={configurationEntityLabel}
          variant="subtle"
        />
      </PageHeader>

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title={t('common:errors.noOrgLinkedTitle')}
            message={t('common:errors.noOrgLinkedMessage')}
          />
        )}

        <ServiceTagsSection organisationId={orgId} />

        <EquipmentDefinitionsSection organisationId={orgId} />

        <CarriersAndRoutesSection organisationId={orgId} />

        <SustainabilitySection organisationId={orgId} />

        {/* Last, and usually absent: the cards below render nothing unless
            the caller is Stratum support. Each asks for itself rather than
            being wrapped here so this stays a list of cards. */}
        <WorkViewTabsSection organisationId={orgId} />

        <PlanFeaturesSection organisationId={orgId} />

        <MenuVisibilitySection organisationId={orgId} />

        <CopyFunctionsSection organisationId={orgId} />

        <ShippingIntegrationSection organisationId={orgId} />
      </main>
    </div>
  );
}
